'use client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Pagination from 'react-bootstrap/Pagination';
import './pagination.scss';

const PaginationComponent = ({
  activePage = 1,
  totalPages,
}: {
  activePage?: number;
  totalPages: number;
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const PAGES_TO_SHOW = 5;

  const updateSearchParam = (page: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('page', page.toString());
    router.push(`${pathname}?${params.toString()}`);
  };

  // Calculate the current group of pages
  const calculatePageGroup = () => {
    const currentGroup = Math.ceil(activePage / PAGES_TO_SHOW);
    const startPage = (currentGroup - 1) * PAGES_TO_SHOW + 1;
    const endPage = Math.min(startPage + PAGES_TO_SHOW - 1, totalPages);
    return { startPage, endPage };
  };

  const { startPage, endPage } = calculatePageGroup();

  // Handle ellipsis clicks
  const handlePrevGroup = () => {
    const newPage = Math.max(1, startPage - PAGES_TO_SHOW);
    updateSearchParam(newPage);
  };

  const handleNextGroup = () => {
    const newPage = Math.min(totalPages, endPage + 1);
    updateSearchParam(newPage);
  };

  const items: React.ReactNode[] = [];

  // First page
  items.push(
    <Pagination.First
      key="first"
      onClick={() => updateSearchParam(1)}
      disabled={activePage === 1}
    />
  );

  // Previous page
  items.push(
    <Pagination.Prev
      key="prev"
      onClick={() => updateSearchParam(activePage - 1)}
      disabled={activePage === 1}
    />
  );

  // Show ellipsis for previous pages if needed
  if (startPage > 1) {
    items.push(
      <Pagination.Item
        key="ellipsis-prev"
        onClick={handlePrevGroup}
        className="ellipsis-item"
      >
        ...
      </Pagination.Item>
    );
  }

  // Current group of pages
  for (let number = startPage; number <= endPage; number++) {
    items.push(
      <Pagination.Item
        key={number}
        active={number === activePage}
        onClick={() => updateSearchParam(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  // Show ellipsis for next pages if needed
  if (endPage < totalPages) {
    items.push(
      <Pagination.Item
        key="ellipsis-next"
        onClick={handleNextGroup}
        className="ellipsis-item"
      >
        ...
      </Pagination.Item>
    );
  }

  // Next page
  items.push(
    <Pagination.Next
      key="next"
      onClick={() => updateSearchParam(activePage + 1)}
      disabled={activePage === totalPages}
    />
  );

  // Last page
  items.push(
    <Pagination.Last
      key="last"
      onClick={() => updateSearchParam(totalPages)}
      disabled={activePage === totalPages}
    />
  );

  return <Pagination>{items}</Pagination>;
};

export default PaginationComponent;
